<template>
  <ValidationObserver ref="observer">
    <div>
      <v-row
        class="d-flex flex-column align-center mx-0"
      >
        <v-col
          cols="12"
          class="py-0 px-2"
        >
          <VTextField
            :id="analysisId"
            v-model="model.measure.value"
            class="analysis--value"
            :placeholder="type !== CALCULATED_ANALYSIS_TYPE ? $t('labWorkOrderTable.writeAValue') : undefined"
            :disabled="!hasPermission('analyses.edit') || isCompletedOrLocked"
            :readonly="type === CALCULATED_ANALYSIS_TYPE"
            :rules="{ required: false, analysisCondition0To14: conditionError}"
            veeValidateInteractionMode="eager"
            formatNumber
            persistentHint
            data-cy="analysisTableCellInput"
            hideDetails
            dense
            @input="submitData"
          >
            <template v-slot:customAppend>
              <div
                v-if="!iconDisabled"
                class="icon--container"
              >
                <v-icon
                  color="#26384B"
                  small
                  :disabled="iconDisabled"
                  @click="onAddNote"
                >
                  {{ iconName }}
                </v-icon>
              </div>
            </template>
            <template
              v-slot:customPrependInner
            >
              <div
                v-if="type === CALCULATED_ANALYSIS_TYPE"
                class="d-flex justify-lg-space-between align-center content"
              >
                <v-btn
                  class="refreshBTN"
                  fab
                  xSmall
                  color="secondary"
                  @click="loadData"
                >
                  <v-icon>
                    mdi-refresh
                  </v-icon>
                </v-btn>
              </div>
              <v-tooltip
                v-if="measure.isPartOfMassBalance"
                top
              >
                <template #activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    class="hover-icon"
                    size="20"
                    v-on="on"
                  >
                    mdi-format-list-bulleted-square
                  </v-icon>
                </template>
                <div class="custom-tooltip">
                  <v-icon
                    style="color: white; padding-right: 25px"
                  >
                    mdi-information
                  </v-icon>

                  <p>{{ $t('dashboard.e4.itemRequired') }}</p>
                </div>
              </v-tooltip>
            </template>
          </VTextField>
          <MeasureNorm
            v-if="hasNormOrNot(measure.norm)"
            :norm="measure.norm"
            :value="measure.value"
            :symbol="measure.symbole"
            :type="type"
            :data-cy="`AnalysisTableCell-${analysisTypeName}`"
          />
          <div v-else>
            <div
              style="width: 100%; height: 25px; text-align: center;"
            >
              <ManualNorm
                v-if="measure.value"
                :value="manualNormColor"
                :isCompletedOrLocked="isCompletedOrLocked"
                @change="onchangeAnalysisNormColor"
              />
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
  </ValidationObserver>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import { debounce, pick, omit } from 'lodash';
import { ValidationObserver } from 'vee-validate';
import NormsMixin from '@/mixins/NormsMixin';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import AddAutomaticSentenceFormVue from '@/components/sites/visitSummaryComponents/AddAutomaticSentenceForm.vue';
import MeasureNorm from '@/views/laboratory/workOrder/summary/MeasureNorm';
import { CALCULATED_ANALYSIS_TYPE } from '@/aquassist-shared/constants/types/componentAnalysisConfiguration';
import ManualNorm from '@/views/laboratory/workOrder/summary/ManualNorm.vue';

export default {
    components: {
        ValidationObserver,
        VTextField,
        MeasureNorm,
        ManualNorm,
    },
    mixins: [
        NormsMixin,
    ],
    props: {
        analysisId: {
            type: String,
            default: '',
        },
        activityId: {
            type: String,
            default: '',
        },
        workOrderId: {
            type: String,
            default: '',
        },
        measure: {
            type: Object,
            default: () => ({}),
        },
        isCompletedOrLocked: {
            type: Boolean,
            default: false,
        },
        type: {
            type: String,
            default: '',
        },
        tableId: {
            type: String,
            default: '',
        },
        analysisTypeName: {
            type: String,
            default: '',
        },
        manualNormColor: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            model: { measure: { value: '' } },
            conditionError: false,
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
        ]),
        colorClass() {
            const { norm } = this.measure;
            const { value } = this.model.measure;

            return this.color(value, norm || {});
        },
        normsRange() {
            const { norm } = this.measure;
            const { veryLow, low, minimum, maximum, high, veryHigh } = norm;

            if (!norm) {
                return '';
            }

            if (this.isDefined(minimum) && this.isDefined(maximum)) {
                return `${minimum} - ${maximum}`;
            }

            if (this.isDefined(veryLow) && this.isDefined(low) && this.isDefined(minimum) && !this.isDefined(maximum)) {
                return `> ${minimum}`;
            }

            if (this.isDefined(maximum) && this.isDefined(high) && this.isDefined(veryHigh) && !this.isDefined(minimum)) {
                return `< ${maximum}`;
            }

            return '';
        },
        iconDisabled() {
            return !this.measure.value;
        },
        hasComment() {
            return this.measure.note;
        },
        iconName() {
            return this.hasComment ? 'chat_bubble' : 'chat_bubble_outline';
        },
        CALCULATED_ANALYSIS_TYPE() {
            return CALCULATED_ANALYSIS_TYPE;
        },
    },
    async created() {
        this.submitData = debounce(this.submitData, 500);
        await this.loadData();
    },
    methods: {
        ...mapActions({
            reloadWorkOrder: 'getWorkOrderInfo',
        }),
        async submitData() {
            const now = new Date();
            const isValid = await this.$refs.observer.validate();
            if (isValid) {
                const bodyParams = {
                    value: this.model.measure.value || null,
                    measuredAt: now.toISOString(),
                    id: this.analysisId,
                };
                const { data } = await this.$rq.updateWorkOrderActivityAnalysisMeasure(
                    this.workOrderId,
                    this.activityId,
                    [bodyParams],
                );
                this.conditionError = !!data?.conditionMatchError;
                if (this.conditionError) {
                    this.$toasted.global.toast_error({
                        message: this.$t('validations.messages.analysisCondition0To14'),
                    });
                }
            }
            this.reloadWorkOrder(this.workOrderId);
            this.$emit('reloadTable');
            this.$emit('reloadNetworkAnalysis');
            if (this.conditionError) {
                await this.loadData();
            }
        },
        async loadData() {
            const norm = pick(this.measure, ['norm']);
            const measure = omit(this.measure, ['norm']);

            this.model = { ...this.model, measure, norm, activity: { id: this.activityId } };
        },
        onAddNote() {
            this.$root.$emit('showModal', {
                title: this.$t('actions.add.note'),
                component: Vue.component('AddAutomaticSentenceFormVue', AddAutomaticSentenceFormVue),
                payload: { measure: this.measure, isAnalysis: true },
                onConfirm: requestPayload => {
                    if (requestPayload !== null) {
                        this.updateMeasureNote(requestPayload);
                    }
                },
            });
        },
        async updateMeasureNote(requestPayload) {
            await this.$rq.updateMeasureNote(
                // eslint-disable-next-line no-underscore-dangle
                this.activityId,
                this.tableId,
                // eslint-disable-next-line no-underscore-dangle
                this.measure.id || this.measure._id,
                requestPayload,
            );
            this.reloadWorkOrder(this.workOrderId);
            this.showMessage();
        },
        showMessage() {
            this.$toasted.global.toast_success({
                message: this.$t('msg.tableUpdated'),
            });
        },
        hasNormOrNot(norm) {
            if (
                !norm.high
                && !norm.veryHigh
                && !norm.veryLow
                && !norm.low
                && !norm.minimum
                && !norm.maximum
            ) {
                return false;
            }
            return true;
        },
        onchangeAnalysisNormColor(item) {
            this.payload = {
                color: item,
                workOrderId: this.workOrderId,
                analysisId: this.analysisId,
                activityId: this.activityId,
            };
            this.$rq.updateManualColorNorm(this.payload);
        },
    },
};
</script>
<style lang="scss" scoped>
@import '@/assets/styles/components/_analysisValue.scss';
    .refreshBTN {
        width: 14px !important;
        height: 14px !important;
        box-shadow: none;
        transform: translate(3px, -1px) !important;
    }
    .refreshBTN i {
        width: 2px;
        font-size: 14px !important;
        padding: 0
    }
    .analysis-value {
        position: relative !important;
        width: 100px;
    }
    .icon--container {
        position: absolute !important;
        bottom: 0px;
        right: 0px;
        width: 20px;
        height: 20px;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .hover-icon {
      border: 1px solid black;
      border-radius: 4px;
      text-align: center;
      transition: color 0.3s;
    }

    .hover-icon:hover {
      color: white;
      background-color: #26384B;
    }

    .custom-tooltip {
      background-color: #26384B;
      color: white;
      border-radius: 4px;
      display: flex;
      flex-direction: row;
      height: 40px;
      width: 250px;
    }

    .v-tooltip__content {
      background-color: #26384B;

    }


</style>
